import { ref } from 'vue';
import { useNotify, NotifyType } from '@/composables/useNotify';
import { useI18n } from 'vue-i18n';
import { hubClient, getApiErrorMessage } from '@tfpl/api-client';
import { getConfig } from '@/config';

export interface VisionPayload {
  collaboratorUuid: string | null;
  message: string;
}

export interface UpdateVisionPayload {
  message: string;
  id: number;
}

export const useUserVision = () => {
  const { notify } = useNotify();
  const { t } = useI18n();
  const loading = ref(false);
  const error = ref();
  const showError = ref(false);
  const setError = (err: boolean): void => {
    showError.value = err;
  };

  const createVision = async (payload: VisionPayload) => {
    loading.value = true;
    error.value = true;
    setError(false);

    try {
      await hubClient.V3CollaboratorVisionService.createVision({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        body: {
          collaboratorUuid: payload.collaboratorUuid as string,
          message: payload.message,
        },
        throwOnError: true,
      });
      notify(t('userGoals.visionCreateSuccess'), NotifyType.SUCCESS);
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('userGoals.visionCreateError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const editVision = async (payload: UpdateVisionPayload) => {
    loading.value = true;
    error.value = true;
    setError(false);

    try {
      await hubClient.V3CollaboratorVisionService.updateVision({
        path: {
          id: payload.id,
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        body: {
          message: payload.message,
        },
        throwOnError: true,
      });
      notify(t('userGoals.visionEditSuccess'), NotifyType.SUCCESS);
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('userGoals.visionEditError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const validateVision = async (visionDraft: string): Promise<hubClient.ValidateVisionResponseDto> => {
    const { data } = await hubClient.V3OpenAiService.validateVision({
      path: {
        tenant: getConfig().TENANT,
        company: getConfig().TENANT,
      },
      body: {
        visionDraft,
      },
      throwOnError: true,
    });
    return data;
  };

  return {
    loading,
    error,
    showError,
    createVision,
    editVision,
    validateVision,
  };
};
